import { GiftCardType } from '@/features/giftcard-self-serve/types/types';
import { classnames, getDateSubtractedByOneSecond, isDateInPast } from '@/helpers';
import useMobileView from '@/hooks/useMobileView';
import { _t, getLocale } from '@/locale/i18n';

const baseTranslationKey = 'components.modules.pages.giftCards.GiftCardDisplay';

const getEllipseStyle = (type: GiftCardType, notValid: boolean) => {
  if (notValid) {
    return 'bg-[#DDE5EB4D]';
  }

  switch (type) {
    case 'wellness':
      return 'bg-[#0F4D4066]';
    case 'universal':
      return 'bg-[#532660]';
    case 'value':
      return 'bg-[#9E7E09]';
    case 'place':
      return 'bg-[#2B639D80]';
    default:
      return 'bg-[#532660]';
  }
};

const getCardStyle = (type: GiftCardType, notValid: boolean) => {
  if (notValid) {
    return 'from-black-100 to-black-300';
  }

  switch (type) {
    case 'wellness':
      return 'bg-brand-900';
    case 'universal':
      return 'from-secondary-700 to-[#532660]';
    case 'value':
      return 'from-[#A38209] to-warning-900';
    case 'place':
      return 'from-information-700 to-[#13467E]';
    default:
      return 'from-secondary-700 to-[#532660]';
  }
};

const GiftCardDisplay = ({
  type,
  title,
  value,
  expiryDate: _validUntil,
  expiryDateTimeTz,
  returnedAt,
}: {
  type: GiftCardType;
  title: string;
  value: number;
  expiryDate: string;
  expiryDateTimeTz: string;
  returnedAt?: string | null;
}) => {
  const { isMobileView } = useMobileView();
  const selectedLang = getLocale();

  const returnedAtObj = returnedAt ? new Date(returnedAt) : null;
  const expiryDate = new Date(getDateSubtractedByOneSecond(expiryDateTimeTz));
  const validUntil = new Date(getDateSubtractedByOneSecond(_validUntil));
  const hasExpired = isDateInPast(expiryDateTimeTz);

  const hasReturned = returnedAtObj !== null;

  const getDateFormat = () => {
    if (hasReturned) {
      return returnedAtObj;
    }
    if (hasExpired) {
      return expiryDate;
    }
    return validUntil;
  };

  const formattedDate = getDateFormat()?.toLocaleDateString(selectedLang, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  });

  const getExpiringString = () => {
    if (hasReturned) {
      return _t(`${baseTranslationKey}.returnedAt`, { date: formattedDate });
    }
    if (hasExpired) {
      return `${_t(`expired`)} ${formattedDate}`;
    }
    return _t(`validUntil`, { dateString: formattedDate });
  };

  return (
    <div
      className={classnames(
        'relative flex max-w-screen-md items-center justify-center overflow-hidden rounded-lg bg-gradient-to-r text-center shadow',
        isMobileView ? 'min-h-[210px]' : 'min-h-[330px]',
        hasExpired || hasReturned ? 'text-fg_negative_bold' : 'text-white',
        getCardStyle(type, hasExpired || hasReturned),
      )}>
      <div
        className={classnames(
          'absolute -right-[400px] -top-[400px] h-[725px] w-[725px] rounded-full',
          getEllipseStyle(type, hasExpired || hasReturned),
        )}
      />
      <div className="gap-xs relative z-10 flex h-full flex-col items-center justify-center">
        <h3 className={isMobileView ? 'text-h-m' : 'text-h-l'}>{title}</h3>
        {!hasExpired && !hasReturned && <span className="text-h-3xl font-bold">{value} kr</span>}
        <span className="text-l">{getExpiringString()}</span>
      </div>
    </div>
  );
};

export default GiftCardDisplay;
